import React, { createRef, useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  CircularProgress,
  Divider,
  Button,
  Box,
  Typography,
  Tooltip,
  Snackbar
} from '@material-ui/core';
import { constants } from 'views/Campaign';
import TemplateEditor from 'components/Template/TemplateEditor';
import SaveMessage from 'views/UserMessaging/customModalContent/saveMessage';
import SaveConfirm from 'views/UserMessaging/customModalContent/saveConfirmTemplate';
import { Alert } from '@material-ui/lab';
import { CloseIcon } from 'components';
import { isReadOnlyMode } from 'store/helpers/helperFunctions';
import { useLanguageSettings } from '../../../Settings/useLanguageSettings';

const { INTERNAL_SURVEY_RESPONSE, EXTERNAL_SURVEY_RESPONSE } = constants.campaignOccurrenceTypes;

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  },
  autoSave: {
    marginLeft: '30%'
  },
  radioLabel: {
    marginLeft: 0,
    alignItems: 'baseline'
  },
  radioGroup: {
    marginLeft: '-1rem'
  },
  cardActions: {
    flexDirection: 'row'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  }
}));

const Design = props => {
  const {
    className,
    staticContext,
    template,
    back,
    showNav,
    isPopupDialog,
    isIFrame,
    ...rest
  } = props;
  const {
    isLoading,
    shouldAutoSave,
    isAutoSaveSuccess,
    isTemplateSaved,
    isTemplateSaving,
    goBack,
    navBackOnError,
    thisTemplate,
    allSurveys,
    handleChange,
    saveTemplate,
    closeTemplate,
    autoSaveTemplate,
    startAutoSaveTimer,
    onFocus,
    onBlur,
    isTemplateReadOnly,
    showWarningMessage,
    isTenantDefaultTemplate,
    modifyPageIterator,
    // Test template props (matches print template pattern)
    onClickTestTemplate,
    onClickTestTemplateClose,
    isTestTemplateLoading,
    isTestDownloadError
  } = template;

  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [isValidationError, setIsValidationError] = useState({});
  const { templateLanguageContent, getTemplateLanguageContent } = useLanguageSettings();
  const [detailsContent, setDetailsContent] = useState(null);

  const classes = useStyles();
  const editorRef = createRef();

  useEffect(() => {
    if (!isLoading) {
      startAutoSaveTimer();
    }
  }, [isLoading]);

  useEffect(() => {
    if(!isIFrame) {
      navBackOnError(back);
    }
  }, [thisTemplate]);

  useEffect(() => {
    if (isTemplateSaved) {
      startAutoSaveTimer();
    }
  }, [isTemplateSaved]);

  useEffect(() => {
    if (shouldAutoSave) {
      editorRef.current.exportAndSaveTemplate();
    }
  }, [shouldAutoSave]);

  useEffect(() => {
    if (templateLanguageContent) {
      setDetailsContent(getTemplateLanguageContent("attachmentTemplateDesign"));
    }
  }, [templateLanguageContent]);

  const onClickSaveButton = () => {
    if (showWarningMessage()) {
      setShowSaveConfirmModal(true);
    } else {
      editorRef.current.exportAndSaveTemplate();
    }
  };

  const saveConfirmTemplate = () => {
    setShowSaveConfirmModal(false);
    editorRef.current.exportAndSaveTemplate();
  };

  const closeSaveConfirm = () => {
    setShowSaveConfirmModal(false);
  };

  const validateErrors = () => {
    let err = Object.values(isValidationError).filter(val => val.isDisabled);
    return err.length > 0 ? true : false;
  };

  /**
   * Handle Test Template click - exports current editor content and tests it
   */
  const handleTestTemplateClick = () => {
    if (editorRef.current && typeof editorRef.current.exportForTest === 'function') {
      editorRef.current.exportForTest((exportedContent) => {
        onClickTestTemplate(exportedContent);
      });
    } else {
      // Fallback if exportForTest is not available
      onClickTestTemplate();
    }
  };

  return (
    <div className={classes.root}>
      {thisTemplate?.eventMetaData ? (
        <Card {...rest} className={clsx(classes.root, className)}>
          <form autoComplete="off">
            {!isPopupDialog && (
              <React.Fragment>
                <div>
                  {isReadOnlyMode() ? (
                    <Alert severity="info">
                      {detailsContent?.readOnlyModeAlert || `You can only view this and do not have access to edit.`}
                    </Alert>
                  ): (
                    isTemplateReadOnly() && (
                      <Alert severity="info">
                        {detailsContent?.templateReadOnlyModeAlert || `You can only view this template and do not have access to edit.`}{' '}
                        {isTenantDefaultTemplate()
                          ? `${detailsContent?.tenantDefaultAlert || `Tenant Default template can be edited only with 'ALL' property.`}`
                          : `${detailsContent?.globalDefaultAlert || `Only admin can edit Global Default template.`}`}
                      </Alert>)
                  )}
                </div>
                <div className={classes.headerContainer}>
                  <div className={classes.title}>
                    <CardHeader
                      title={detailsContent?.pageTitle || `Attachment Design`}
                      titleTypographyProps={{ variant: 'h1' }}
                    />
                  </div>
                  {!isIFrame && (
                    <CloseIcon onClickClose={closeTemplate} />
                  )}
                </div>
              </React.Fragment>
            )}
            <Divider />
            <CardContent>
              {shouldAutoSave && (
                <Typography
                  color="textSecondary"
                  variant="body2"
                  className={classes.autoSave}>
                  {detailsContent?.autoSavingLabel || `AutoSaving...`}
                </Typography>
              )}
              {thisTemplate.eventMetaData && (
                <TemplateEditor
                  ref={editorRef}
                  thisTemplate={thisTemplate}
                  surveys={(
                    thisTemplate.campaignOccurrenceType !==
                    INTERNAL_SURVEY_RESPONSE &&
                    thisTemplate.campaignOccurrenceType !==
                    EXTERNAL_SURVEY_RESPONSE)
                      ? allSurveys
                      : []
                  }
                  useUnsubscribeLinkOption={false}
                  useSurveysLinkOption={false}
                  templateKeys={thisTemplate.eventMetaData}
                  handleChange={handleChange}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  saveTemplate={saveTemplate}
                  shouldAutoSave={shouldAutoSave}
                  autoSaveTemplate={autoSaveTemplate}
                  isTemplateReadOnly={isTemplateReadOnly}
                  modifyPageIterator={modifyPageIterator}
                  setIsValidationError={setIsValidationError}
                />
              )}
            </CardContent>
            {(back || showNav || isPopupDialog) && (
              <>
                <Divider />
                <CardActions className={classes.cardActions}>
                  {!isPopupDialog && !isIFrame && (
                    <Button
                      color="secondary"
                      variant="contained"
                      disabled={isLoading}
                      link={back}
                      onClick={goBack}>
                      {detailsContent?.backButtonLabel || `Back`}
                    </Button>
                  )}
                  <Typography></Typography>
                  <Box flexGrow={1}>&nbsp;</Box>
                  {/* Test Template Button - only enabled for PDF type attachments */}
                  {thisTemplate?.type === 'PDF' && (
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={isLoading || isTestTemplateLoading || isTemplateSaving}
                      onClick={handleTestTemplateClick}
                      style={{ marginRight: '8px' }}>
                      {detailsContent?.testTemplateButtonLabel || `Test Template`}
                    </Button>
                  )}
                  {!isTemplateReadOnly() &&
                    (validateErrors() ? (
                      <Tooltip title={detailsContent?.saveTemplateErroHelper || `Fix the handlebar formats`}>
                        <span>
                          <Button
                            color="primary"
                            variant="contained"
                            disabled={() => validateErrors}
                            onClick={() => onClickSaveButton()}>
                            {detailsContent?.saveTemplateButtonLabel || `Save Attachment`}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={isLoading || isTemplateSaving}
                        onClick={() => onClickSaveButton()}>
                        {detailsContent?.saveTemplateButtonLabel || `Save Attachment`}
                      </Button>
                    ))}
                </CardActions>
              </>
            )}
          </form>
        </Card>
      ) : (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      )}
      {!isAutoSaveSuccess && <SaveMessage openSnackbar={isTemplateSaved} />}
      {<Snackbar
        open={isTestDownloadError}
        autoHideDuration={6000}
        onClose={onClickTestTemplateClose}>
        <Alert onClose={onClickTestTemplateClose} severity="warning">
          {detailsContent?.testTemplateFailedMessage || `Test Template Failed`}
        </Alert>
      </Snackbar>}
      {showSaveConfirmModal && (
        <SaveConfirm
          closeModel={closeSaveConfirm}
          saveConfirmTemplate={saveConfirmTemplate}></SaveConfirm>
      )}
    </div>
  );
};

export default Design;
